import { useState, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { setQuery } from '@/features/filterSlice';
import { useNavigate } from 'react-router-dom';
import { update as updateMapLayerConfig } from '@/features/mapLayerSlice';
import { LayerType } from '@/views/map/layers/types';
import { WITH_OVERHANG } from '@/features/types';
import isEqual from 'lodash/isEqual';
import Modal from '@/components/Modals/Modal';
import { useSmallScreen } from '@/hooks/useWindowDimension';
import { closeFilterForm } from '@/features/analysisMapSlice';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import type { RedFlagWarning } from '@/features/wildfireSlice';
import type { FilterState } from '@/features/filterSlice';
import { useFilterLogic } from './useFilterLogic';
import { FilterForm } from './FilterForm';
import { SpanCountWithDisplay } from './SpanCountWithDisplay';

interface FilterProps {
  onClose?: () => void;
  redFlagWarnings?: RedFlagWarning[];
}

const Filter = (props: FilterProps) => {
  const { onClose: propsOnClose } = props;
  const intl = useClientTerminology();
  const isSmallScreen = useSmallScreen();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const filterState = useAppSelector((state) => state.filterQuery);
  const { isFilterForm, currentViewMode } = useAppSelector(
    (state) => state.analysisMap
  );
  const projectId = currentViewMode.modeDetails?.id ?? null;
  const [internalFilterState, setInternalFilterState] =
    useState<FilterState | null>(null);

  const handleSubmit = useCallback(
    (formData: FilterState) => {
      if (!isEqual(formData.encroachmentType, filterState.encroachmentType)) {
        const types = formData.encroachmentType;
        // Only set showOverhang when exactly one type is selected; both or neither = no filter
        const showOverhang =
          types.length === 1 ? types[0] === WITH_OVERHANG : undefined;
        dispatch(
          updateMapLayerConfig({
            [LayerType.encroachment]: { showOverhang },
          })
        );
      }

      // Set new filter state after any filter state/form comparisons
      dispatch(setQuery(formData));

      if (propsOnClose) {
        propsOnClose();
      } else {
        navigate(-1);
      }
    },
    [dispatch, filterState.encroachmentType, propsOnClose, navigate]
  );

  const { formProps, formData, fieldFlags, isFetching } = useFilterLogic({
    initialFilterState: filterState,
    redFlagWarnings: props.redFlagWarnings,
    onSubmit: handleSubmit,
    onFilterUpdated: setInternalFilterState,
  });

  return (
    <Modal
      opened={isFilterForm}
      onClose={() => dispatch(closeFilterForm())}
      title={intl.formatMessage({ id: 'filter.title' })}
      fullScreen={isSmallScreen}
      primaryButtonTestId="filter-show-results-button"
      primaryLabel={intl.formatMessage({ id: 'filter.showResults' })}
      onPrimaryAction={formProps.handleSubmit}
      secondaryButtonTestId="clear-filter-results-button"
      secondaryLabel={intl.formatMessage({ id: 'filter.clear' })}
      onSecondaryAction={formProps.clearForm}
      footerElements={
        <SpanCountWithDisplay
          filterState={internalFilterState}
          projectId={projectId}
        />
      }
      testId="filter-modal"
    >
      <FilterForm
        register={formProps.register}
        control={formProps.control}
        sortedSpecies={formData.sortedSpecies}
        tags={formData.tags}
        worklog={formData.worklogStatusOptions}
        redFlagWarnings={formData.redFlagWarnings}
        hasWildfireRisk={fieldFlags.hasWildfireRisk}
        hasHazardTrees={fieldFlags.hasHazardTrees}
        hasStrikeTrees={fieldFlags.hasStrikeTrees}
        hasCriticalTrees={fieldFlags.hasCriticalTrees}
        hasOverhang={fieldFlags.hasOverhang}
        hasUpdatedAtYear={fieldFlags.hasUpdatedAtYear}
        isFetching={isFetching}
      />
    </Modal>
  );
};

export default Filter;
